import { Link } from 'gatsby'
import { ChevronRight } from 'src/components/Icons/ChevronRight'
import { Image } from 'src/components/ui/Image'
import { useUnmissableSection } from 'src/graphql/queries/UnmissableSection'
import { useMobile } from 'src/hooks/useMobile'
import { dispatchAmplitudeEvent } from 'src/utils/amplitude'
import type { StandardObject } from 'src/utils/amplitude/types/amplitudeObjects'
import { stripUrl } from 'src/utils/stripUrl'

type UnmissableSectionProps = {
  onClose: () => void
  standardObj: StandardObject | null
}

export const UNMISSABLE_SECTION_ID = 'modal-search-unmissable-section'

const UnmissableSection = ({
  onClose,
  standardObj,
}: UnmissableSectionProps) => {
  const { isMobile } = useMobile()
  const data = useUnmissableSection()

  if (!data) {
    return null
  }

  const { collectionCard, boxCards, highlightedCard } = data

  return (
    <div
      id={UNMISSABLE_SECTION_ID}
      data-testid="discovery-hub-unmissable"
      className="min-h-[382px] pr-4 pb-4 restructure-medium-desktop:min-w-[507px] restructure-medium-desktop:w-[40%] restructure-medium-desktop:pl-20 restructure-medium-desktop:pt-10 restructure-medium-desktop:pr-0 restructure-small-desktop:bg-[#F7F8F9] restructure-medium-desktop:min-h-[402px]"
    >
      <div className="unmissable-container restructure-medium-desktop:flex restructure-medium-desktop:flex-col restructure-medium-desktop:justify-center restructure-medium-desktop:mr-auto restructure-medium-desktop:max-w-[347px]">
        <h3 className="desktop-body-regular-text1 text-restructure-primary mt-8 mb-4 restructure-small-desktop:mb-[15px] restructure-medium-desktop:mt-0">
          Imperdível
        </h3>
        <div className="font-inter">
          <div className="flex gap-[10px]">
            {collectionCard.map((card) => {
              const { title, image, altText, link } = card

              return (
                <Link
                  key={title}
                  className="w-1/3 flex flex-col items-center justify-center restructure-small-desktop:gap-0 bg-white p-4 h-[119px] restructure-medium-desktop:h-[133.99px]"
                  to={link}
                  onClick={() => {
                    onClose()
                    dispatchAmplitudeEvent({
                      eventName: 'Click Icons',
                      eventData: {
                        ...standardObj,
                        click_name: title,
                      },
                    })
                  }}
                >
                  <Image
                    width={75}
                    height={75}
                    src={stripUrl(image)}
                    alt={altText ?? title}
                  />
                  <p
                    className="font-medium font-inter text-font-size-2 text-[#000000] text-center"
                    dangerouslySetInnerHTML={{ __html: title }}
                  />
                </Link>
              )
            })}
          </div>

          {highlightedCard?.active && (
            <Link
              className="mt-8 restructure-small-desktop:mt-2 border border-solid border-[#D9DDE1] rounded-[4px] bg-white px-4 py-[16.44px] flex flex-row items-center justify-between restructure-medium-desktop:border-0 h-[82.88px]"
              to="/campanha/produtos-azuis-decathlon"
              onClick={() => {
                onClose()
                dispatchAmplitudeEvent({
                  eventName: 'Click Icons',
                  eventData: {
                    ...standardObj,
                    'click name': highlightedCard.title + highlightedCard.text,
                  },
                })
              }}
            >
              <div className="flex flex-row items-center gap-4">
                <Image
                  width={50}
                  height={50}
                  src={stripUrl(highlightedCard.image)}
                  alt={highlightedCard?.altText ?? highlightedCard.title}
                />
                <p className="text-font-size-3 text-secondary font-semibold">
                  <b
                    dangerouslySetInnerHTML={{ __html: highlightedCard.title }}
                  />
                  <br />
                  <span
                    className="font-normal text-font-size-2 text-tertiary"
                    dangerouslySetInnerHTML={{ __html: highlightedCard.text }}
                  />
                </p>
              </div>
              {isMobile && (
                <ChevronRight width={24} height={24} color="#3643BA" />
              )}
            </Link>
          )}

          <div className="flex gap-2 mt-[8.12px]">
            {boxCards.map((card) => {
              const { title, image, altText, link } = card

              return (
                <Link
                  key={title}
                  className={`w-1/${boxCards.length} h-24 border border-solid border-gray bg-white rounded-sm p-4 flex flex-col justify-center gap-2 restructure-medium-desktop:border-0`}
                  to={link}
                  onClick={() => {
                    onClose()
                    dispatchAmplitudeEvent({
                      eventName: 'Click Icons',
                      eventData: {
                        ...standardObj,
                        'click name': title,
                      },
                    })
                  }}
                >
                  <div className="w-full">
                    <Image
                      width={16}
                      height={16}
                      src={stripUrl(image)}
                      alt={altText ?? title}
                    />
                  </div>
                  <p
                    className="-tracking-normal min-h-11 font-medium font-inter text-font-size-3 text-[#000000] restructure-small-desktop:text-left restructure-small-desktop:w-full"
                    dangerouslySetInnerHTML={{ __html: title }}
                  />
                </Link>
              )
            })}
          </div>
        </div>
      </div>
    </div>
  )
}

export default UnmissableSection
